import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Table, Tag, message } from 'antd';
import { FiRefreshCw } from 'react-icons/fi';
import DashboardPageWrapper from '../../../../components/layout/DashboardPageWrapper';
import tradeCommitmentService from '../../../../services/tradeCommitmentService';
import { apiErrMsg, formatMoney } from './tradeCommitment.helpers';

const SOURCE_LABELS = {
  commitment: 'Commitment',
  purchase_deal_sheet: 'PDS',
  order: 'Order',
};

const navigateToRecord = (navigate, row) => {
  if (row.source === 'purchase_deal_sheet') {
    navigate(`/pre-ipo/purchase-deal-sheet/${row.record_id}`);
    return;
  }
  if (row.source === 'order') {
    navigate(`/pre-ipo/order-management/${row.record_id}`);
    return;
  }
  navigate(`/pre-ipo/trade-commitments/${row.record_id}`);
};

const buildLedgerColumns = (navigate) => [
  { title: 'Entity', dataIndex: 'entity', key: 'entity' },
  { title: 'Share', dataIndex: 'share', key: 'share' },
  {
    title: 'Quantity',
    dataIndex: 'quantity',
    key: 'quantity',
    align: 'right',
    render: (v) => Number(v).toLocaleString('en-IN'),
  },
  {
    title: 'Gross',
    dataIndex: 'gross_amount',
    key: 'gross_amount',
    align: 'right',
    render: (v) => formatMoney(v),
  },
  {
    title: 'Paid',
    dataIndex: 'paid_amount',
    key: 'paid_amount',
    align: 'right',
    render: (v) => formatMoney(v),
  },
  {
    title: 'Outstanding',
    dataIndex: 'outstanding_amount',
    key: 'outstanding_amount',
    align: 'right',
    render: (v) => formatMoney(v),
  },
  { title: 'Ref', dataIndex: 'ref', key: 'ref' },
  {
    title: 'Source',
    dataIndex: 'source',
    key: 'source',
    render: (v) => <Tag>{SOURCE_LABELS[v] || v}</Tag>,
  },
  {
    title: '',
    key: 'actions',
    render: (_, row) => (
      <Button type="link" onClick={() => navigateToRecord(navigate, row)}>
        View
      </Button>
    ),
  },
];

function LedgerSection({ title, rows, total, navigate }) {
  return (
    <div className="mb-8">
      <h2 className="mb-3 text-lg font-semibold">{title}</h2>
      <Table
        rowKey={(row) => `${row.source}-${row.ref}`}
        columns={buildLedgerColumns(navigate)}
        dataSource={rows}
        pagination={false}
        size="small"
        bordered
        summary={() =>
          rows.length > 0 ? (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <strong>Total outstanding</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <strong>{formatMoney(total)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} colSpan={3} />
            </Table.Summary.Row>
          ) : null
        }
      />
    </div>
  );
}

export default function TradeCommitmentsSummaryPage() {
  const navigate = useNavigate();
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [linkingKey, setLinkingKey] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await tradeCommitmentService.getLedgerSummary();
      setLedger(data);
    } catch (err) {
      setError(apiErrMsg(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleLinkDuplicate = async (pair) => {
    const key = `${pair.commitment_id}-${pair.target_id}`;
    setLinkingKey(key);
    try {
      if (pair.target_type === 'purchase_deal_sheet') {
        await tradeCommitmentService.linkToPurchaseDealSheet(
          pair.commitment_id,
          pair.target_id,
        );
      } else {
        await tradeCommitmentService.linkToOrder(pair.commitment_id, pair.target_id);
      }
      message.success(`Linked ${pair.commitment_ref} to ${pair.target_ref}`);
      load();
    } catch (err) {
      message.error(apiErrMsg(err));
    } finally {
      setLinkingKey('');
    }
  };

  return (
    <DashboardPageWrapper title="Trade Commitments Ledger">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          Settlement view: outstanding receivables and payables from orders, purchase deal sheets, and
          unlinked provisional commitments.
        </p>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/pre-ipo/trade-commitments')}>All commitments</Button>
          <Button icon={<FiRefreshCw />} onClick={load} loading={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {error && <Alert type="error" message={error} className="mb-4" showIcon />}

      {ledger?.possible_duplicates?.length > 0 && (
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="Possible duplicate records — link to avoid double counting"
          description={
            <ul className="mt-2 space-y-2 pl-0 list-none">
              {ledger.possible_duplicates.map((pair) => {
                const key = `${pair.commitment_id}-${pair.target_id}`;
                return (
                  <li key={key} className="flex flex-wrap items-center gap-2">
                    <span>
                      {pair.commitment_ref} ↔ {pair.target_ref}
                      {' · '}
                      {formatMoney(pair.outstanding_amount)} outstanding
                      {' · '}
                      <Tag color={pair.confidence === 'high' ? 'green' : 'orange'}>
                        {pair.confidence === 'high' ? 'High confidence' : 'Possible match'}
                      </Tag>
                    </span>
                    <Button
                      size="small"
                      type="primary"
                      loading={linkingKey === key}
                      onClick={() => handleLinkDuplicate(pair)}
                    >
                      Link
                    </Button>
                  </li>
                );
              })}
            </ul>
          }
        />
      )}

      <LedgerSection
        title="Receivables"
        rows={ledger?.receivables?.rows || []}
        total={ledger?.receivables?.total || 0}
        navigate={navigate}
      />

      <LedgerSection
        title="Payables"
        rows={ledger?.payables?.rows || []}
        total={ledger?.payables?.total || 0}
        navigate={navigate}
      />

      {ledger && (
        <Alert
          type="info"
          showIcon
          message={`Net (receivables − payables): ${formatMoney(ledger.net)}`}
        />
      )}
    </DashboardPageWrapper>
  );
}
